/**
 * APEX v7.7.2 — Game-level Insights : Catégorie B (timing objectifs)
 *
 * Patterns B1-B8 contextualisés (référence PRD section 13.3).
 * Liste objectifs 2026 : Dragon, Baron, Rift Herald, Voidgrubs.
 * Atakhan RETIRÉ au patch 26.1.
 *
 * Données utilisées (v7.7.2 parser extension) :
 *   - game.objectiveEvents : [{ type:'monster'|'building', subType, minute, second, isMyTeam }]
 *   - game.deathDetails    : mes morts
 *   - game.timeFrames      : gold/xp/cs @15 (pour contextualiser déficit)
 *
 * Note : certaines contextualisations fines (position des 5 joueurs, push de tour
 * en temps réel) nécessiteraient les participantFrames complets. En v7.7.2 on livre
 * une version contextualisée "best effort" basée sur ce qu'on a.
 */

function objSeconds(ev) {
  return (ev.minute || 0) * 60 + (ev.second || 0);
}

function labelObjective(subType) {
  const map = {
    DRAGON: 'le Dragon',
    RIFTHERALD: 'le Rift Herald',
    BARON_NASHOR: 'le Baron',
    HORDE: 'les Voidgrubs',
    ELDER_DRAGON: 'le Dragon Ancestral'
  };
  return map[subType] || "l'objectif";
}

/**
 * Estime le gold-diff de l'équipe à un instant T (approximation via timeFrames @15).
 * Retourne null si on n'a pas l'info.
 */
function teamGoldDiffApprox(game) {
  const me = game.timeFrames?.min15?.my;
  const opp = game.timeFrames?.min15?.opp;
  if (!me || !opp) return null;
  // Approximation simpliste : diff de gold lane * 5 (très grossier, à raffiner v7.8)
  return ((me.gold || 0) - (opp.gold || 0));
}

// ============================================================
// B2 — Drake-rush opportuniste (positif)
// ============================================================

function detectOpportunisticObjective(game) {
  const objEvents = game.objectiveEvents || [];
  const myObjectives = objEvents.filter(e => e.type === 'monster' && e.isMyTeam);
  if (myObjectives.length === 0) return null;

  // Heuristique : objectif pris dans les 90s après un avantage (on n'a pas les kills
  // précis ici, donc on note juste un objectif majeur pris tôt comme bon tempo)
  const baron = myObjectives.find(o => o.subType === 'BARON_NASHOR');
  if (baron) {
    return {
      id: 'B2_baron_secured',
      category: 'B',
      severity: 'positive',
      title: 'Baron sécurisé',
      short: `Baron pris à ${baron.minute}min`,
      long: `Ton équipe a pris le Baron à ${baron.minute}:${String(baron.second).padStart(2,'0')}. Bon objectif, exploite la pression qui suit pour fermer la game.`,
      context: { minute: baron.minute }
    };
  }
  return null;
}

// ============================================================
// B4 — Soul/Elder loupé (contextualisé)
// ============================================================

function detectSoulOrElderLost(game) {
  const objEvents = game.objectiveEvents || [];
  const enemyDragons = objEvents.filter(e =>
    e.type === 'monster' && e.subType === 'DRAGON' && !e.isMyTeam
  );
  const enemyElder = objEvents.find(e =>
    e.type === 'monster' && e.subType === 'ELDER_DRAGON' && !e.isMyTeam
  );

  // Soul = 4 dragons élémentaires pour l'équipe ennemie
  if (enemyDragons.length >= 4 || enemyElder) {
    const goldDiff = teamGoldDiffApprox(game);
    let context, severity;

    if (goldDiff !== null && goldDiff < -2000) {
      context = `Vous étiez en déficit (~${Math.abs(Math.round(goldDiff))} gold lane @15). Contester aurait été risqué.`;
      severity = 'info';
    } else if (goldDiff !== null && goldDiff > 1500) {
      context = `Vous aviez pourtant un avantage (~${Math.round(goldDiff)} gold lane @15). Rotation/macro à revoir.`;
      severity = 'critical';
    } else {
      context = `Game serrée. L'âme/Elder concédé pèse lourd en late.`;
      severity = 'warning';
    }

    const label = enemyElder ? 'le Dragon Ancestral' : "l'âme du Dragon (Soul)";
    return {
      id: 'B4_soul_elder_lost',
      category: 'B',
      severity,
      title: enemyElder ? 'Elder concédé' : 'Soul concédé',
      short: `L'ennemi a pris ${label}`,
      long: `L'équipe ennemie a sécurisé ${label}. ${context}`,
      context: { enemy_dragons: enemyDragons.length, has_elder: !!enemyElder, gold_diff_15: goldDiff }
    };
  }
  return null;
}

// ============================================================
// B6 — Tour offerte (mort qui précède la chute d'une tour)
// ============================================================

function detectTowerGivenAway(game) {
  const deaths = game.deathDetails || [];
  const objEvents = game.objectiveEvents || [];
  if (deaths.length === 0) return null;

  // Tours alliées tombées (building, pas mon équipe = tour à moi détruite)
  const myTowersLost = objEvents.filter(e =>
    e.type === 'building' && !e.isMyTeam
  );
  if (myTowersLost.length === 0) return null;

  for (const death of deaths) {
    const deathSec = (death.minute || 0) * 60 + (death.second || 0);
    for (const tower of myTowersLost) {
      const towerSec = objSeconds(tower);
      const gap = towerSec - deathSec;
      if (gap >= 0 && gap <= 30) {
        return {
          id: 'B6_tower_given_away',
          category: 'B',
          severity: 'warning',
          title: 'Tour offerte',
          short: `Mort ${gap}s avant chute d'une tour`,
          long: `Ta mort à ${death.minute}:${String(death.second).padStart(2,'0')} a précédé la chute d'une de tes tours ${gap}s plus tard. Ta mort a ouvert le territoire.`,
          context: { gap_seconds: gap, minute: death.minute }
        };
      }
    }
  }
  return null;
}

// ============================================================
// B-synthèse — Contrôle des objectifs sur la game
// ============================================================

function detectObjectiveControl(game) {
  const objEvents = game.objectiveEvents || [];
  const monsters = objEvents.filter(e => e.type === 'monster');
  if (monsters.length === 0) return null;

  const mine = monsters.filter(m => m.isMyTeam).length;
  const enemy = monsters.filter(m => !m.isMyTeam).length;
  const total = mine + enemy;
  if (total < 3) return null; // pas assez d'objectifs pour conclure

  const ratio = mine / total;
  if (ratio >= 0.7) {
    return {
      id: 'B_objective_dominance',
      category: 'B',
      severity: 'positive',
      title: 'Contrôle des objectifs',
      short: `${mine}/${total} objectifs pris`,
      long: `Ton équipe a contrôlé les objectifs (${mine} sur ${total}). C'est souvent ce qui fait gagner — bonne macro.`,
      context: { mine, enemy, total }
    };
  } else if (ratio <= 0.3) {
    return {
      id: 'B_objective_deficit',
      category: 'B',
      severity: 'warning',
      title: 'Objectifs cédés',
      short: `Seulement ${mine}/${total} objectifs`,
      long: `Ton équipe n'a pris que ${mine} objectifs sur ${total}. Le manque de contrôle macro coûte des games, même en stompant ta lane.`,
      context: { mine, enemy, total }
    };
  }
  return null;
}

// ============================================================
// API publique
// ============================================================

export function analyzeObjectives(game, context = {}) {
  if (!game) return [];
  const insights = [];

  const preObjLost = detectSoulOrElderLost(game);
  if (preObjLost) insights.push(preObjLost);

  const towerGiven = detectTowerGivenAway(game);
  if (towerGiven) insights.push(towerGiven);

  const opportunistic = detectOpportunisticObjective(game);
  if (opportunistic) insights.push(opportunistic);

  const control = detectObjectiveControl(game);
  if (control) insights.push(control);

  return insights;
}
